import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * ErrorBoundary — v1.8.4
 * Prevents blank-screen crashes on React errors.
 * Shows friendly fallback with reload button + stack trace in collapsible details.
 */
export class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        this.setState({ errorInfo });
        // Log for debugging — visible in browser console
        // eslint-disable-next-line no-console
        console.error("[ErrorBoundary]", error, errorInfo);
    }

    handleReload = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
        if (this.props.onReset) this.props.onReset();
        else window.location.reload();
    };

    render() {
        if (!this.state.hasError) return this.props.children;
        const label = this.props.label || "Komponen ini";
        return (
            <div
                className="rounded-xl p-6 m-4"
                style={{
                    background: "rgba(239, 68, 68, 0.06)",
                    border: "1px solid rgba(239, 68, 68, 0.25)",
                }}
                data-testid="error-boundary-fallback"
            >
                <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 flex-shrink-0" style={{ color: "var(--apple-red)" }} />
                    <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm mb-1" style={{ color: "var(--text-primary)" }}>
                            {label} tidak bisa ditampilkan
                        </div>
                        <div className="text-xs mb-3" style={{ color: "var(--text-secondary)" }}>
                            Terjadi error tidak terduga. Silakan reload halaman. Jika terus terjadi, kirim screenshot layar ini ke admin.
                        </div>
                        <div className="flex items-center gap-2 mb-3">
                            <button
                                onClick={this.handleReload}
                                data-testid="error-boundary-reload"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white"
                                style={{ background: "var(--apple-red)" }}
                            >
                                <RefreshCw className="w-3.5 h-3.5" /> Reload
                            </button>
                        </div>
                        <details className="text-[11px]" style={{ color: "var(--text-muted)" }}>
                            <summary className="cursor-pointer select-none hover:underline">Detail teknis (buat admin)</summary>
                            <pre className="mt-2 p-2 rounded bg-slate-50 overflow-x-auto font-mono whitespace-pre-wrap break-words" style={{ color: "var(--text-secondary)" }}>
                                {String(this.state.error?.toString() || "")}
                                {this.state.errorInfo?.componentStack || ""}
                            </pre>
                        </details>
                    </div>
                </div>
            </div>
        );
    }
}

export default ErrorBoundary;
